import { useState } from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import axiosClient from '../lib/api/axiosClient';

export default function PrivacyPolicy() {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleUnsubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus('idle');

        try {
            const response = await axiosClient.post('/unsubscribe', { email });

            if (response.status !== 200) {
                console.error('Unsubscribe error:', response.data);
                throw new Error('Unsubscribe failed');
            }
            
            setStatus('success');
            setEmail('');
        } catch (error) {
            console.error('Error:', error);
            setStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen pt-16">
            <div className="max-w-4xl mx-auto px-4 py-12">
                <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>

                <div className="prose max-w-none">
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">Our Commitment to Privacy</h2>
                        <p>
                            EmploSkills is committed to protecting and respecting your privacy. This policy explains how we collect, 
                            use, and protect your personal information when you use our services.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">Information Collection and Use</h2>
                        <p>
                            We collect and use your personal information to:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>Administer your account</li>
                            <li>Provide the products and services you've requested</li>
                            <li>Send you important updates about our services</li>
                            <li>Contact you about our products and services that may interest you</li>
                            <li>Improve our services and user experience</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">Communications</h2>
                        <p>
                            When you provide your contact information, we may send you:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>Service notifications and important updates</li>
                            <li>Educational content and resources</li>
                            <li>Information about new features and services</li>
                            <li>Marketing communications (if you've consented)</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">Data Protection</h2>
                        <p>
                            We implement appropriate technical and organizational measures to protect your personal information 
                            against unauthorized access, alteration, disclosure, or destruction.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
                        <p>
                            You have the right to:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>Access your personal information</li>
                            <li>Correct inaccurate information</li>
                            <li>Request deletion of your information</li>
                            <li>Withdraw your consent for marketing communications</li>
                            <li>Request a copy of your data</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">Unsubscribe from Communications</h2>
                        <p className="mb-4">
                            You can unsubscribe from our marketing communications at any time using the form below. 
                            You will continue to receive important service-related communications.
                        </p>
                        
                        <form onSubmit={handleUnsubscribe} className="max-w-md">
                            {status === 'success' && (
                                <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-md flex items-center">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Successfully unsubscribed from marketing communications.
                                </div>
                            )}

                            {status === 'error' && (
                                <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md flex items-center">
                                    <AlertCircle className="w-5 h-5 mr-2" />
                                    Something went wrong. Please try again.
                                </div>
                            )}

                            <div className="mb-4">
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                                    placeholder="your@email.com"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full px-4 py-2 text-white font-medium rounded-md ${
                                    isSubmitting 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-primary hover:bg-primary-dark'
                                }`}
                            >
                                {isSubmitting ? 'Processing...' : 'Unsubscribe'}
                            </button>
                        </form>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
                        <p>
                            If you have any questions about our privacy practices or would like to exercise your rights, 
                            please contact us at <a href="mailto:contact@emploskills.com" className="text-primary hover:text-primary-dark">
                            contact@emploskills.com</a>
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
} 